/**
 * Canvas Layout Configuration for Wire Interfaces Step
 *
 * The Wire Interfaces canvas uses percentage-based positioning for responsive
 * layout. All x, y, w, h values are percentages of the canvas container.
 *
 * Layout regions (visual reference):
 *
 *     ┌─────────────────────────────────────────────────────────────────┐
 *     │                     SIGNAL ARC (top, curved)                    │
 *     │                    [signalArc.startX → endX]                    │
 *     │                                                                 │
 *     │   ┌─────────────┐      ┌─────────────────┐      ┌────────────┐ │
 *     │   │  External   │      │                 │      │  External  │ │
 *     │   │   Funds     │      │   SMART ASSET   │      │  Accounts  │ │
 *     │   │  (leftBox)  │      │      CORE       │      │ (rightBox) │ │
 *     │   └─────────────┘      └─────────────────┘      └────────────┘ │
 *     │                                                                 │
 *     │   ┌─────────────┐      ┌────────┐ ┌────────┐                   │
 *     │   │   Payment   │      │ Logic  │ │ Disburse│                  │
 *     │   │   Buffers   │      │ Module │ │  Gate   │                  │
 *     │   │  (payment)  │      │(gateL) │ │(gateR)  │                  │
 *     │   └─────────────┘      └────────┘ └────────┘                   │
 *     └─────────────────────────────────────────────────────────────────┘
 *
 * To customize layout, adjust the percentage values below.
 */

/** Rectangle region definition (percentage-based) */
export interface CanvasRegion {
  /** X position from left edge (%) */
  readonly x: number;
  /** Y position from top edge (%) */
  readonly y: number;
  /** Width (%) */
  readonly w: number;
  /** Height (%) */
  readonly h: number;
}

/** Signal arc configuration for curved connections at top */
export interface SignalArcConfig {
  /** Start X position (%) */
  readonly startX: number;
  /** End X position (%) */
  readonly endX: number;
  /** Base Y position (%) */
  readonly baseY: number;
  /** Arc curve amplitude (%) */
  readonly amplitude: number;
}

/** Complete canvas layout configuration */
export interface CanvasLayoutConfig {
  /** Central Smart Asset core region */
  readonly core: CanvasRegion;
  /** Left panel for value inflows (External Funds) */
  readonly leftBox: CanvasRegion;
  /** Right panel for value outflows (External Accounts) */
  readonly rightBox: CanvasRegion;
  /** Bottom-left panel for payment buffers/credits */
  readonly payment: CanvasRegion;
  /** Bottom gate for logic module (left of center) */
  readonly gateLeft: CanvasRegion;
  /** Bottom gate for disbursement (right of center) */
  readonly gateRight: CanvasRegion;
  /** Signal arc configuration for oracle/data connections */
  readonly signalArc: SignalArcConfig;
}

/**
 * Default canvas layout configuration
 *
 * Uses percentage-based positioning for responsive layout.
 * Core is centered, with value flows on sides and signals on top arc.
 */
export const CANVAS_LAYOUT: CanvasLayoutConfig = {
  // Central Smart Asset core - centered horizontally, upper-middle vertically
  core: { x: 34, y: 36, w: 32, h: 22 },

  // Value flow panels - positioned on left/right sides
  leftBox: { x: 8, y: 44, w: 18, h: 12 }, // External Funds (value_in)
  rightBox: { x: 74, y: 44, w: 18, h: 12 }, // External Accounts (value_out)

  // Payment and gate regions - bottom area
  payment: { x: 8, y: 68, w: 24, h: 18 }, // Payment Buffers (charge)
  gateLeft: { x: 38, y: 72, w: 18, h: 8 }, // Logic Module gate
  gateRight: { x: 58, y: 72, w: 18, h: 8 }, // Disbursement gate

  // Signal arc - curved path at top for oracle/signal connections
  signalArc: { startX: 38, endX: 62, baseY: 18, amplitude: 6 },
};

/** Canvas grid background style */
export const CANVAS_GRID_STYLE: React.CSSProperties = {
  backgroundColor: "var(--sa-bg)",
  backgroundImage: "radial-gradient(rgba(148, 163, 184, 0.25) 1px, transparent 1px)",
  backgroundSize: "24px 24px",
};
